// The start screen: the logo, and under it the user's picture and the name others see (none: a guest in
// each meeting), a new meeting — a "пространство" — one by its link or id, the account's meetings, and its
// access code; the documents' links below. The first visit — and a new edition of the documents — asks for
// them to be accepted before anything else: the user agreement and, a mark of its own, the consent to the
// processing of personal data. While the documents are hidden (legal.ts), it does not.
import { useEffect, useRef, useState } from "preact/hooks";
import { rename } from "../account.ts";
import { accept, accepted, DOCUMENTS } from "../legal.ts";
import { forget, meet, newMeeting, openLink } from "../meeting.ts";
import { changed, state } from "../store.ts";
import { Access } from "./Access.tsx";
import { Status } from "./App.tsx";
import { Avatar } from "./Avatar.tsx";
import { IconButton } from "./Icon.tsx";
import { LegalLinks } from "./Legal.tsx";

export function Start() {
  return accepted() ? <Begin /> : <Welcome />;
}

/** The documents accepted: the name, a new meeting, one by its link, the account's meetings. */
function Begin() {
  const [name, setName] = useState(state.name);
  const nameField = useRef<HTMLInputElement>(null);
  const linkField = useRef<HTMLInputElement>(null);
  const joining = state.wanted !== null;
  // The account's name may come after the page shows: another device's account.
  useEffect(() => {
    if (name === "" && state.name !== "") setName(state.name);
  }, [state.name]);
  useEffect(() => (joining ? nameField : linkField).current?.focus(), []);
  /** What the user asked for, with the name in the field — as it is now, typed or filled in. */
  const go = (act: () => void) => {
    rename((nameField.current?.value ?? name).trim());
    act();
  };
  const enter = () => go(() => meet(state.wanted!));
  const open = () => go(() => openLink(linkField.current?.value ?? ""));
  return (
    <div class="start">
      <img class="start-logo" src="/logo-navy.png" alt="Veyumo" width={226} height={48} />
      <p class="tagline">Место для встреч: голос, видео, чат и&nbsp;расшифровки</p>
      <div class="card">
        <div class="start-head">
          <h1>{joining ? "Вход в пространство" : "Добро пожаловать"}</h1>
          <button
            class="start-avatar"
            title="Аватар и имя"
            disabled={!state.up}
            onClick={() => {
              state.dialog = { kind: "profile" };
              changed();
            }}
          >
            <Avatar id={state.user} size={44} />
          </button>
        </div>
        <label for="name">Имя, которое увидят участники (можно не давать)</label>
        <input
          id="name"
          ref={nameField}
          autocomplete="nickname"
          maxLength={40}
          placeholder="Без имени — гость"
          value={name}
          onInput={(e) => setName(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && (joining ? enter() : go(newMeeting))}
        />
        {joining ? (
          <button class="primary" onClick={enter}>
            Войти в пространство
          </button>
        ) : (
          <>
            <button class="primary" onClick={() => go(newMeeting)}>
              Новое пространство
            </button>
            <div class="or">или</div>
            <div class="row">
              <input
                id="link"
                ref={linkField}
                placeholder="Ссылка или код"
                aria-label="Ссылка или код пространства"
                autocapitalize="off"
                spellcheck={false}
                onKeyDown={(e) => e.key === "Enter" && open()}
              />
              <button class="soft" onClick={open}>
                Войти
              </button>
            </div>
          </>
        )}
        <Status />
        <Mine open={(id) => go(() => meet(id))} />
        <Access />
      </div>
      <LegalLinks />
    </div>
  );
}

/** The first visit, or a new edition of the documents: what Veyumo is, and the documents accepted — the
 * agreement and, apart, the consent — before anything else. A meeting's link opened by one with a name goes
 * in once they are. */
function Welcome() {
  const [terms, setTerms] = useState(false);
  const [consent, setConsent] = useState(false);
  const go = () => {
    accept();
    if (state.wanted === null || state.name === "") return;
    state.joining = false;
    if (state.up) meet(state.wanted);
  };
  return (
    <div class="start">
      <img class="start-logo" src="/logo-navy.png" alt="Veyumo" width={226} height={48} />
      <p class="tagline">Место для встреч: голос, видео, чат и&nbsp;расшифровки</p>
      <div class="card welcome">
        <h1>Добро пожаловать</h1>
        <p>
          Veyumo работает без регистрации: ни почты, ни телефона, ни пароля — аккаунт хранит этот браузер. Чтобы начать,
          примите условия.
        </p>
        <label class="check">
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.currentTarget.checked)} />
          <span>
            Принимаю{" "}
            <a href={DOCUMENTS[0].href} target="_blank" rel="noopener">
              Пользовательское соглашение
            </a>
          </span>
        </label>
        <label class="check">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.currentTarget.checked)} />
          <span>
            Даю{" "}
            <a href={DOCUMENTS[2].href} target="_blank" rel="noopener">
              согласие на обработку персональных данных
            </a>{" "}
            на условиях{" "}
            <a href={DOCUMENTS[1].href} target="_blank" rel="noopener">
              Политики
            </a>
          </span>
        </label>
        <p class="note">
          Файлов cookie и аналитики нет: браузер хранит только ключ аккаунта, имя и настройки —{" "}
          <a href={DOCUMENTS[3].href} target="_blank" rel="noopener">
            подробнее
          </a>
          .
        </p>
        <button class="primary" disabled={!terms || !consent} onClick={go}>
          Продолжить
        </button>
      </div>
      <LegalLinks />
    </div>
  );
}

/** The account's meetings, the last first — all the server keeps, a hundred, in a list that scrolls past five
 * or so: a click opens one; an ended one, which nobody enters any more, the user removes from the list. */
function Mine({ open }: { open: (id: string) => void }) {
  if (state.mine.length === 0) return null;
  return (
    <div class="mine">
      <div class="section">Мои пространства</div>
      <div class="mine-list">
        {state.mine.map((m) => (
          <div key={m.id} class="mine-row">
            <button class="meeting-row" disabled={m.ended} onClick={() => open(m.id)}>
              <span class="title">{m.title}</span>
              <span class="meta">
                {[m.ended && "закрыто", when(m.at), m.host && "вы владелец"].filter((x) => x).join(" · ")}
              </span>
            </button>
            {m.ended && <IconButton icon="trash" title={`Убрать «${m.title}» из списка`} onClick={() => forget(m.id)} />}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A time of the user's day: today's hour, or the day. */
function when(at: number): string {
  const d = new Date(at);
  const hm = d.toLocaleTimeString("ru", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === new Date().toDateString()) return `сегодня ${hm}`;
  return `${d.toLocaleDateString("ru", { day: "numeric", month: "short" })} ${hm}`;
}
